import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import {
  defaultLocale,
  getLocaleDirection,
  getMessagesForLocale,
  isSupportedLocale,
  type PortalLocale,
} from "@/lib/i18n/portal";

type MessageCatalog = Record<string, unknown>;
type TranslationValues = Record<string, string | number | null | undefined>;

type I18nContextValue = {
  locale: PortalLocale;
  messages: MessageCatalog;
};

const I18nContext = createContext<I18nContextValue | null>(null);

function getResolvedLocale(locale: string): PortalLocale {
  return isSupportedLocale(locale) ? locale : defaultLocale;
}

function getMessage(messages: MessageCatalog, key: string) {
  return key.split(".").reduce<unknown>((current, segment) => {
    if (!current || typeof current !== "object" || !(segment in current)) {
      return undefined;
    }

    return (current as Record<string, unknown>)[segment];
  }, messages);
}

/** `{name, plural, one {# order} other {# orders}}` — the ICU plural subset.
 * Delegates category selection to Intl.PluralRules so Arabic gets its six
 * forms (zero/one/two/few/many/other) rather than an English-shaped
 * `count === 1` guess. `#` expands to the value. */
const PLURAL_RE = /\{(\w+),\s*plural,\s*((?:\s*\w+\s*\{[^{}]*\})+)\s*\}/g;

/**
 * Egypt's tag, for the same reason `lib/formatting/currency.ts` uses it: `Intl`
 * resolves a bare `ar` to the `latn` numbering system, `ar-EG` to `arab`.
 */
const ARABIC_LOCALE = "ar-EG";

function formatCount(locale: PortalLocale, count: number): string {
  const resolved = locale === "ar" ? ARABIC_LOCALE : locale;
  return new Intl.NumberFormat(resolved).format(count);
}

function selectPlural(
  locale: PortalLocale,
  count: number,
  arms: string,
): string {
  const branches: Record<string, string> = {};
  for (const [, name, body] of arms.matchAll(/(\w+)\s*\{([^{}]*)\}/g)) {
    branches[name] = body;
  }
  // Exact-value arms (`=0 {...}`) are written as `_0` here because the key
  // pattern above is \w+; check the literal count first, then the category.
  const category = new Intl.PluralRules(locale).select(count);
  const arm = branches[`_${count}`] ?? branches[category] ?? branches.other ?? "";
  // `#` expands through the locale's number formatter, not String(count).
  // String(count) always yields Latin digits, so an Arabic portal rendered
  // "5 طلبات" directly beneath "٩٩٠ ج.م" — two numeral systems in one card, and
  // "5.0 من ٥" mixing both inside a single string. Arabic here means ar-EG:
  // `Intl` resolves a bare `ar` to the latn numbering system, which is the very
  // thing this is fixing (same reason lib/formatting/currency.ts pins ar-EG).
  return arm.replace(/#/g, formatCount(locale, count));
}

export function interpolate(
  template: string,
  values?: TranslationValues,
  locale: PortalLocale = defaultLocale,
) {
  if (!values) {
    return template;
  }

  const pluralised = template.replace(PLURAL_RE, (match, key, arms) => {
    const value = values[key];
    return typeof value === "number"
      ? selectPlural(locale, value, arms)
      : match;
  });

  return pluralised.replace(/\{(\w+)\}/g, (match, key) => {
    const value = values[key];
    return value === null || value === undefined ? match : String(value);
  });
}

export function SellerIntlProvider({
  children,
  locale,
}: {
  children: ReactNode;
  locale: string;
}) {
  const resolvedLocale = getResolvedLocale(locale);
  const value = useMemo<I18nContextValue>(
    () => ({
      locale: resolvedLocale,
      messages: getMessagesForLocale(resolvedLocale) as MessageCatalog,
    }),
    [resolvedLocale],
  );

  useEffect(() => {
    document.documentElement.lang = resolvedLocale;
    document.documentElement.dir = getLocaleDirection(resolvedLocale);
  }, [resolvedLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

function useI18nContext() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("SellerIntlProvider is required");
  }

  return context;
}

export function useLocale() {
  return useI18nContext().locale;
}

export function useTranslations() {
  const { messages, locale } = useI18nContext();

  return (key: string, values?: TranslationValues) => {
    const message = getMessage(messages, key);
    if (typeof message !== "string") {
      return key;
    }

    return interpolate(message, values, locale);
  };
}

/**
 * Translate a key for an explicit locale, independent of the current UI locale.
 * Used where one screen needs both language versions of a string at once — e.g.
 * a share sheet offering "copy the English post" / "copy the Arabic post".
 */
export function translateForLocale(
  locale: PortalLocale,
  key: string,
  values?: TranslationValues,
): string {
  const messages = getMessagesForLocale(locale) as MessageCatalog;
  const message = getMessage(messages, key);
  if (typeof message !== "string") {
    return key;
  }
  return interpolate(message, values, locale);
}
